import { go } from '../../App';
import { useAllPages } from '../../data';
import { Loading, PageHead, Panel, StatusChip, fmtMoney, formatWhen } from '../../ui';
import { Booking, Money } from '../../types';

// Guests (canon 09 hotel "Guests" · Register template). DERIVED from the hotel's
// own bookings — the same own-partition projection as Reports, grouped by guest.
// No guest store, no change to the booking-create contract: a first-class guest
// record is §4.1 "later". Stop at the derived view. Each row's drawer lists that
// guest's bookings, each opening the EXISTING /bookings/{id} drawer.

interface GuestGroup {
  name: string;
  bookings: Booking[];
  room?: string;
  trips: number;
  latest: string;
  spend: Money;
}

function group(bookings: Booking[]): GuestGroup[] {
  const by = new Map<string, Booking[]>();
  for (const b of bookings) {
    const name = (b.guestName ?? '').trim();
    if (!name) continue;
    if (!by.has(name)) by.set(name, []);
    by.get(name)!.push(b);
  }
  const out: GuestGroup[] = [];
  for (const [name, list] of by) {
    list.sort((a, b) => b.start.localeCompare(a.start));
    const currency = list.find((b) => b.guestAmount)?.guestAmount?.currency ?? 'EUR';
    // Committed spend: what a confirmed or completed trip actually costs the
    // guest. A declined or cancelled request is not spend.
    const spendMinor = list
      .filter((b) => (b.status === 'confirmed' || b.status === 'completed') && b.guestAmount)
      .reduce((sum, b) => sum + b.guestAmount!.minor, 0);
    out.push({
      name,
      bookings: list,
      room: list.find((b) => b.guestRoom)?.guestRoom,
      trips: list.length,
      latest: list[0]!.start,
      spend: { minor: spendMinor, currency },
    });
  }
  out.sort((a, b) => a.name.localeCompare(b.name));
  return out;
}

export function Guests({ openGuest }: { openGuest?: string }) {
  const { items, error, loading } = useAllPages<Booking>('/bookings');
  const guests = group(items);
  const selectedName = openGuest ? decodeURIComponent(openGuest) : undefined;
  const selected = selectedName ? guests.find((g) => g.name === selectedName) : undefined;

  return (
    <div>
      <PageHead title="Guests" sub="Everyone you've booked for, drawn from your bookings." />

      {error && <p className="error-line">{error}</p>}
      {loading && items.length === 0 ? (
        <Loading />
      ) : guests.length === 0 ? (
        <div className="empty-neutral">No guests yet — they appear here as you book for them.</div>
      ) : (
        <Panel style={{ overflow: 'hidden' }} flush meta={`${guests.length} guest${guests.length === 1 ? '' : 's'}`}>
          <table>
            <thead>
              <tr>
                <th>Guest</th>
                <th>Room</th>
                <th className="num">Trips</th>
                <th>Latest</th>
                <th className="num">Guest spend</th>
              </tr>
            </thead>
            <tbody>
              {guests.map((g) => (
                <tr key={g.name} className="click" onClick={() => go(`/guests/${encodeURIComponent(g.name)}`)}>
                  <td>{g.name}</td>
                  <td className="mono">{g.room ?? '—'}</td>
                  <td className="num">{g.trips}</td>
                  <td className="mono">{formatWhen(g.latest)}</td>
                  <td className="num mono">{g.spend.minor ? fmtMoney(g.spend) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      {selectedName && (
        <GuestDrawer name={selectedName} group={selected} />
      )}
    </div>
  );
}

function GuestDrawer({ name, group }: { name: string; group?: GuestGroup }) {
  const close = () => go('/guests');
  return (
    <>
      <div className="drawer-veil" onClick={close} />
      <aside className="drawer" role="dialog" aria-label="Guest detail">
        <div className="scroll">
          <h1 style={{ marginTop: 4 }}>{name}</h1>
          {!group ? (
            <p className="hint">No bookings for this guest.</p>
          ) : (
            <>
              <p className="subline">
                {group.trips} trip{group.trips === 1 ? '' : 's'}
                {group.room ? ` · room ${group.room}` : ''}
                {group.spend.minor ? ` · ${fmtMoney(group.spend)} committed` : ''}
              </p>
              <h2 style={{ fontSize: 'var(--text-lg)', color: 'var(--text-secondary)', margin: '12px 0 8px' }}>Bookings</h2>
              <div className="kv">
                {group.bookings.map((b) => (
                  <div className="kvrow click" key={b.id} onClick={() => go(`/bookings/${b.id}`)} style={{ cursor: 'pointer' }}>
                    <span>
                      <StatusChip status={b.status} />
                      <span className="hint" style={{ display: 'block', marginTop: 4 }}>
                        {b.productName ?? b.productId} · {b.supplierName}
                      </span>
                    </span>
                    <span style={{ textAlign: 'right' }}>
                      <span className="v mono" style={{ fontSize: 'var(--text-xs)' }}>{formatWhen(b.start)}</span>
                      <br />
                      <span className="money" style={{ fontSize: 'var(--text-xs)' }}>{fmtMoney(b.guestAmount)}</span>
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="pinned">
          <button className="btn secondary" onClick={close} style={{ flex: 1 }}>Close</button>
        </div>
      </aside>
    </>
  );
}
